import dayjs from 'dayjs';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { InfoItem } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { useBuyBackEventByIdQuery } from '@/modules/issuer-dashboard/queries/buyBack.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { BaseModalProps, ModalContentWrapper, Spinner } from 'ui';
import { goodFormatUSDCurrency } from 'ui/utils';
import { BuyBackEventCalculateResponse } from 'api';

interface IssuerBuyBackOperationInfoModalProps extends BaseModalProps {
  item: BuyBackEventCalculateResponse;
  isDeleted?: boolean;
}

export const IssuerBuyBackOperationInfoModal = ({
  item,
  isDeleted = false,
  open,
  onClose,
}: IssuerBuyBackOperationInfoModalProps) => {
  const [params] = useSearchParams();
  const { pathname } = useLocation();

  const { isLoading } = useBuyBackEventByIdQuery({
    id: item.id,
    is_deleted: isDeleted,
  });

  return (
    <ModalContentWrapper variant="right" title="Buy back info" open={open} onClose={onClose}>
      {isLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && item && (
        <div className="flex flex-col justify-between gap-4 h-full">
          <div className="space-y-3">
            <InfoItem title="ID">
              <div>#{item.id}</div>
            </InfoItem>
            {/* <InfoItem title="Status"> */}
            {/*   <ActivityStatusBadge status={item.status} /> */}
            {/* </InfoItem> */}
            <InfoItem title="Class">
              <Link
                state={{ from: pathname + '?' + params.toString() }}
                to={{
                  pathname: issuerDashboardPaths.dynamic.classById(
                    (item?.register_class_id || '0').toString(),
                  ),
                  search: params.toString(),
                }}
                className="text-blue-700 hover:underline"
              >
                {item.register_class?.class_name ?? '-'}
              </Link>
            </InfoItem>

            <InfoItem title="Accepted offers">
              <div className="text-[12px] text-nowrap">
                {item.current_buy_back_offer ?? 0} / {item.total_buy_back_offer}
              </div>
            </InfoItem>

            <InfoItem title="Term">
              <div className="">{`${dateToPrint(
                item.open_event_date,
                DateVariant.Table,
                false,
              )} - ${dateToPrint(
                dayjs(item.close_event_date || '')
                  .add(1, 'minute')
                  .toISOString(),
                DateVariant.Table,
                false,
              )}`}</div>
            </InfoItem>

            <InfoItem title="$ per share">
              <div>{goodFormatUSDCurrency(item.price_per_share)}</div>
            </InfoItem>
            <InfoItem title="Holder confirmations">
              <div>{formatNumberWithCommas(item.total_holder_quantity)}</div>
            </InfoItem>

            <InfoItem title="Total">
              <span>{`$${formatNumberWithCommas(
                Math.round(item.total_buy_back_offer * 100) / 100,
              )}`}</span>
            </InfoItem>

            <InfoItem title="Memo">
              <div>{item.memo || '-'}</div>
            </InfoItem>
          </div>
        </div>
      )}
    </ModalContentWrapper>
  );
};
